import type { AtlantisOperation, AtlantisProjectReport } from "./atlantis-output";
import type {
  TerraformEntry,
  TerraformSelectionOrigin,
  TerraformSelectionReason,
} from "./terraform-differ";

/**
 * The terraform panel's wire format.
 *
 * Three authorities feed it and none of them is CHEW's opinion. Per-project run
 * state comes from the commit statuses Atlantis writes against the head commit;
 * locks come from Atlantis's own lock API; the projects, their entry points and
 * which of them this change has to plan come from the differ, which reads them
 * out of `atlantis.yaml` and the terraform itself.
 *
 * CHEW contributes exactly one thing of its own — the claim that Atlantis's
 * autoplan would miss some of those projects — and labels it as such.
 */

export type TerraformProjectState =
  | "not-planned"
  | "queued"
  | "planning"
  | "planned"
  | "no-changes"
  | "plan-failed"
  | "applying"
  | "applied"
  | "apply-failed"
  | "stale";

export type TerraformStatusState = "pending" | "success" | "failure" | null;

export interface TerraformCommitStatus {
  state: TerraformStatusState;
  description: string | null;
  /**
   * Where Atlantis is running this — its own job page, with the live log.
   *
   * Atlantis puts the job URL on the status the moment a project starts, so
   * this is the only pointer at a run in flight that exists outside Atlantis.
   */
  url: string | null;
  /** When Atlantis last wrote this status, so a newer request can outrank it. */
  at: string | null;
}

export interface TerraformLock {
  /** Atlantis lock id, which is also what its UI needs to release one. */
  id: string;
  project: string | null;
  dir: string;
  workspace: string;
  pullNumber: number | null;
  pullUrl: string | null;
  user: string;
  lockedAt: string;
  /** True when the lock belongs to a different pull request than the one shown. */
  foreign: boolean;
}

export type TerraformLocksAvailability = "available" | "unconfigured" | "unreachable";

/**
 * The projection in two layers, because they cost wildly different amounts.
 *
 * The selection layer follows from the commit alone: read the terraform at one
 * ref, run the differ, cache the answer against the diff. The state layer is
 * four more calls to GitHub and Atlantis — commit statuses, every Atlantis
 * comment on the pull request, the lock API — and it changes every few
 * seconds, so it can neither be cached nor waited for.
 *
 * Asking for both at once is why the panel used to take so long to show
 * anything: the slowest authority set the time to first paint, and a reviewer
 * who only wanted to know which stacks a change touches paid for Atlantis's
 * comment history to find out. They are now fetched separately and the panel
 * draws as soon as the first arrives.
 */

/** One project the differ selected, before anything is known about its run. */
export interface TerraformSelectedProject {
  project: string;
  dir: string;
  workspace: string;
  stack: string;
  environment: string | null;
  entry: TerraformEntry;
  origin: TerraformSelectionOrigin;
  reasons: TerraformSelectionReason[];
  /** Whether Atlantis's own autoplan would have selected this project. */
  autoplanned: boolean;
  /** Projects that must apply before this one, from `depends_on` in atlantis.yaml. */
  dependsOn: string[];
}

export interface TerraformSelectionProjection {
  repository: string;
  pullNumber: number;
  pullUrl: string;
  title: string;
  headSha: string;
  /** The commit the diff is measured from, which is the differ's other key. */
  baseSha: string;
  /**
   * Environments the repository has, in the order their projects appear in
   * `atlantis.yaml`. Read from the differ rather than fixed, so a repository
   * with one environment or four is shown as it is.
   */
  environments: string[];
  /** Stacks that this change touches, in apply order. */
  stacks: string[];
  projects: TerraformSelectedProject[];
  /** Projects CHEW selected that Atlantis's autoplan would have skipped. */
  autoplanGap: string[];
  /** What the differ could not make sense of. Shown, not logged. */
  warnings: string[];
  atlantisUrl: string | null;
  /** True when the viewer may leave Atlantis commands on this pull request. */
  canComment: boolean;
}

/** What Atlantis is doing to one project right now. */
export interface TerraformProjectRunState {
  state: TerraformProjectState;
  planStatus: TerraformCommitStatus;
  applyStatus: TerraformCommitStatus;
  /** Detail parsed out of Atlantis's comments, when a recognisable one exists. */
  plan: AtlantisProjectReport | null;
  apply: AtlantisProjectReport | null;
  lock: TerraformLock | null;
  /** The operation Atlantis is running, or has been asked to run, right now. */
  activeOperation: AtlantisOperation | null;
  /** Atlantis's job page for the run in flight, when its status names one. */
  jobUrl: string | null;
  /** When CHEW's comment asked for the run that has not been answered yet. */
  requestedAt: string | null;
}

export interface TerraformStateProjection {
  /** The head this state was read against, so a stale answer can be discarded. */
  headSha: string;
  /** Project name to its run state. A project Atlantis has not touched is absent. */
  states: Record<string, TerraformProjectRunState>;
  locks: TerraformLock[];
  locksAvailability: TerraformLocksAvailability;
  locksUnavailableReason: string | null;
}

/** One project as the panel shows it: what the differ said, plus what Atlantis says. */
export interface TerraformProjectRow extends TerraformSelectedProject, TerraformProjectRunState {}

/** The merged view the panel renders. */
export interface TerraformProjection extends Omit<TerraformSelectionProjection, "projects"> {
  projects: TerraformProjectRow[];
  locks: TerraformLock[];
  locksAvailability: TerraformLocksAvailability;
  locksUnavailableReason: string | null;
  /** False until Atlantis's own answer has arrived, so the panel can say so. */
  stateLoaded: boolean;
}

/**
 * Run state for a project nothing is known about yet.
 *
 * Before Atlantis's layer arrives every project reads as `not-planned`, which
 * is the same thing the panel shows for a project Atlantis has genuinely never
 * run. `stateLoaded` on the projection is what tells those two apart, so the
 * panel can hold back a "plan the ones Atlantis missed" button it has no basis
 * for yet.
 */
export const unknownRunState: TerraformProjectRunState = {
  state: "not-planned",
  planStatus: { state: null, description: null, url: null, at: null },
  applyStatus: { state: null, description: null, url: null, at: null },
  plan: null,
  apply: null,
  lock: null,
  activeOperation: null,
  jobUrl: null,
  requestedAt: null,
};

/** Put the two layers together, with whatever of the second has arrived. */
export function mergeTerraformProjection(
  selection: TerraformSelectionProjection,
  state: TerraformStateProjection | undefined,
): TerraformProjection {
  const { projects, ...rest } = selection;
  // State read against a different head belongs to a commit that has moved on.
  // Showing it beside this commit's selection would put a stale plan next to a
  // project that has since changed, which is the one error the panel exists to
  // prevent.
  const current = state && state.headSha === selection.headSha ? state : undefined;
  return {
    ...rest,
    projects: projects.map((project) => ({
      ...project,
      ...(current?.states[project.project] ?? unknownRunState),
    })),
    locks: current?.locks ?? [],
    locksAvailability: current?.locksAvailability ?? "available",
    locksUnavailableReason: current?.locksUnavailableReason ?? null,
    stateLoaded: current !== undefined,
  };
}

const applyStates: TerraformProjectState[] = ["applying", "applied", "apply-failed"];

export const terraformStateLabel: Record<TerraformProjectState, string> = {
  "not-planned": "not planned",
  queued: "queued",
  planning: "planning",
  planned: "planned",
  "no-changes": "no changes",
  "plan-failed": "plan failed",
  applying: "applying",
  applied: "applied",
  "apply-failed": "apply failed",
  stale: "stale",
};

/**
 * Whether Atlantis has been asked for a run it has not reported on yet.
 *
 * Atlantis answers a comment by running somewhere else and writing back later,
 * so between the two there is a window in which nothing on the pull request has
 * changed. Saying "queued" through that window is the difference between a page
 * that looks broken and one that looks busy.
 */
export interface TerraformRunRequest {
  plan: boolean;
  apply: boolean;
}

/**
 * The one state a project shows in the matrix.
 *
 * Whatever is happening now outranks whatever happened before: a pending status
 * first, then a run Atlantis owes an answer for, and only then the last result.
 * Otherwise a re-plan of a project that failed an hour ago reads as still
 * failed for as long as Atlantis takes to pick the job up.
 *
 * Between the settled states, apply outranks plan — once an apply has run the
 * plan that produced it is spent and the reader needs a different action from
 * it. A plan CHEW can see in a comment but which has no commit status against
 * the current head is `stale`: the head moved, and Atlantis will refuse that
 * saved plan file.
 */
export function terraformProjectState(input: {
  planStatus: TerraformCommitStatus;
  applyStatus: TerraformCommitStatus;
  plan: AtlantisProjectReport | null;
  apply: AtlantisProjectReport | null;
  requested?: TerraformRunRequest;
}): TerraformProjectState {
  if (input.applyStatus.state === "pending") return "applying";
  if (input.planStatus.state === "pending") return "planning";
  if (input.requested?.apply || input.requested?.plan) return "queued";
  if (input.applyStatus.state === "failure") return "apply-failed";
  if (input.applyStatus.state === "success") return "applied";
  if (input.planStatus.state === "failure") return "plan-failed";
  if (input.planStatus.state === "success") {
    return input.plan?.outcome === "no-changes" ? "no-changes" : "planned";
  }
  // No status against this head. A parsed result means the plan belongs to an
  // earlier commit; nothing at all means Atlantis has not run here.
  return input.plan || input.apply ? "stale" : "not-planned";
}

/** The states that mean something is happening to this project right now. */
const runningStates: TerraformProjectState[] = ["queued", "planning", "applying"];

export const terraformStateIsRunning = (state: TerraformProjectState): boolean => runningStates.includes(state);

export const terraformStateIsApply = (state: TerraformProjectState): boolean => applyStates.includes(state);

/** Atlantis writes one commit status per project, named for the operation. */
export function atlantisStatusContext(operation: "plan" | "apply", project: string): string {
  return `atlantis/${operation}: ${project}`;
}
